import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Truck, FileText, AlertCircle, CheckCircle2, Clock, X, Camera, Upload, Trash2, ClipboardCheck, ChevronRight, ChevronLeft, User, Calendar, Activity, Shield, Settings, AlertTriangle, CheckSquare } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { DocumentViewer } from './DocumentViewer';
import clsx from 'clsx';

interface Operator {
  id: string;
  name: string;
  rut: string;
  active: boolean;
}

interface Maquinaria {
  id: string;
  codigo: string;
  nombre: string;
  tipo: string;
  patente: string | null;
  marca: string | null;
  modelo: string | null;
  anio: number | null;
  clave_qr: string;
}

interface Horometro {
  lectura: number;
  alerta_mantenimiento: number;
}

interface Documento {
  id: string;
  tipo: string;
  storage_path: string | null;
  fecha_vencimiento: string | null;
}

interface VerificationForm {
  accessCode: string;
  nombre: string;
  rut: string;
}

interface AnomaliaForm {
  descripcion: string;
  fotos: File[];
}

interface ChecklistItem {
  id: string;
  nombre: string;
  estado: 'bueno' | 'regular' | 'malo' | null;
  observaciones: string;
  categoria: string;
}

// Categorías del checklist
const CHECKLIST_CATEGORIES = [
  {
    id: 'seguridad',
    name: 'Riesgos Materiales de Seguridad',
    icon: Shield,
    color: 'red',
    isSecurityRisk: true
  },
  {
    id: 'estado_vuelta',
    name: 'Estado Después de la 1ª Vuelta',
    icon: Activity,
    color: 'orange',
    isSecurityRisk: true
  },
  {
    id: 'otros_controles',
    name: 'Otros Controles',
    icon: Settings,
    color: 'yellow',
    isSecurityRisk: true
  },
  {
    id: 'operacional',
    name: 'Condición Operacional',
    icon: CheckSquare,
    color: 'blue',
    isSecurityRisk: false
  },
  {
    id: 'estructural',
    name: 'Condición Estructural',
    icon: Settings,
    color: 'purple',
    isSecurityRisk: false
  }
];

const RIESGOS_MATERIALES = [
  'Inspección de pre-uso realizada, con elementos críticos de la lista operativos y en buen estado',
  'Prueba de frenos y dirección realizada',
  'Dispositivo de Fatiga y Somnolencia (Gauss) se encuentra operativo',
  '¿Sabe cómo utilizar el sistema SmartCap? Y ¿Qué debe hacer según cada nivel de alerta?',
  '¿Se encuentra en condiciones físicas y psicológicamente óptimas (fatiga)?',
  '¿Sabe que no debe utilizar dispositivos móviles mientras opera el equipo?',
  '¿Conoce las velocidades máximas de donde va a conducir?',
  'Sistema de proximidad (ECAS) se encuentra operativo',
  '¿Sabe cómo utilizar el sistema ECAS? Y ¿Sabe qué debe hacer según cada nivel de alarma?',
  '¿Tiene Radio de comunicación, encontrándose encendida y operativa? ¿Se encuentra en el canal correcto?',
  '¿Conoce cómo se realiza la comunicación por la radio? Y ¿Conoce cómo se debe responder al momento de ser contactado por otra persona a través de la radio de comunicación (protocolo de comunicación)',
  '¿Recibió su capacitación teórica y práctica para operar este equipo?',
  '¿Cuenta con licencia Municipal y licencia interna vigentes?',
  '¿Se encuentra apto para operar? (Simel y Psicosensométrico)'
];

const ESTADO_DESPUES_VUELTA = [
  'En caminos interiores de la fundición, botadero y patios, se encuentran los pretiles laterales y/o centrales con la altura de acuerdo al estándar (la altura de la mitad del neumático)',
  'En botadero, patios, se cuenta con pretil de referencia de acuerdo al estándar',
  'En los caminos interiores de la fundición, botadero y patios, existe señalética de regulación de tránsito visible, que se pueda leer e interpretar'
];

const OTROS_CONTROLES = [
  'Sistema protección Contra Incendio (AFEX) operativo. ¿Verificó que pasador de seguro de sistema AFEX se encuentra en la posición adecuada?',
  'Escalera / Psamano / Baranda / Grating',
  'Sistema Protección Contra Incendio (Extintor) // Con recarga vigente (Fecha)'
];

const CONDICION_OPERACIONAL = [
  'Parabrisas delantero y trasero',
  'Limpiaparabrisas',
  'Luces trabajo delanteras y traseras',
  'Puerta vidrio lado derecho',
  'Puerta vidrio lado izquierdo',
  'Base y espejo retrovisor derecho',
  'Base y espejo retrovisor izquierdo',
  'Acceso lado derecho e izquierdo',
  'Protección parabrisas delantero',
  'Radio transmisor y micrófono',
  'Baliza y base de sustentación',
  'Triangulo, conos',
  'Asiento conductor',
  'Cinturones de Seguridad',
  'Extintor y base de sustentación',
  'Aire acondicionado',
  'Palancas, botones y controles',
  'Housekeeping',
  'Sistema AFEX',
  'Cámara 360°',
  'Joystick',
  'Horómetro',
  'Marcador 1° aceite de motor',
  'Marcador nivel aceite de motor',
  'Marcador presión hidráulico',
  'Marcador nivel de combustible',
  'Ducto admisión de aire',
  'Tapa de radiador, motor, baterías',
  'Tapa estanque hidráulico',
  'Batería'
];

const CONDICION_ESTRUCTURAL = [
  'Estado del motor de traslación',
  'Estado del motor de rotación',
  'Bocina',
  'Estructura exterior en general',
  'Sistema de traslación',
  'Oruga derecha',
  'Oruga izquierda',
  'Reductores',
  'Polines',
  'Tornamesa',
  'Zapatas',
  'Aguilón',
  'Tuberías',
  'Flexibles',
  'Pasadores',
  'Cilindro de levante',
  'Cilindro de volteo',
  'Cilindro articulación intermedia',
  'Punto de engrase',
  'Martillo',
  'Flexibles',
  'Pasadores',
  'Conexión carga de nitrógeno',
  'Tapa inferior cuña',
  'Cuña',
  'Pernos',
  'Aspectos operativos',
  'Trabajo de dirección/levante',
  'Bocina de retroceso',
  'Sistema eléctrico'
];

const calculateNextMaintenance = (currentReading: number, interval: number): number => {
  return currentReading + (interval - (currentReading % interval));
};

const formatDate = (date: string | null) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const getDocumentStatus = (doc: Documento) => {
  if (!doc.storage_path) return { status: 'missing', color: 'red', text: 'Sin archivo' };
  if (!doc.fecha_vencimiento) return { status: 'valid', color: 'green', text: 'Vigente' };
  
  const vencimiento = new Date(doc.fecha_vencimiento);
  const today = new Date();
  const diffDays = Math.ceil((vencimiento.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { status: 'expired', color: 'red', text: 'Vencido' };
  if (diffDays <= 30) return { status: 'warning', color: 'yellow', text: 'Por vencer' };
  return { status: 'valid', color: 'green', text: 'Vigente' };
};

const validateRut = (rut: string): boolean => {
  rut = rut.replace(/\./g, '').replace(/-/g, '');
  
  if (!/^\d{7,8}[0-9kK]$/i.test(rut)) {
    return false;
  }
  
  const rutDigits = rut.slice(0, -1);
  const verifier = rut.slice(-1).toLowerCase();
  
  let sum = 0;
  let multiplier = 2;
  
  for (let i = rutDigits.length - 1; i >= 0; i--) {
    sum += parseInt(rutDigits[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const expectedVerifier = 11 - (sum % 11);
  let calculatedVerifier: string;
  
  if (expectedVerifier === 11) calculatedVerifier = '0';
  else if (expectedVerifier === 10) calculatedVerifier = 'k';
  else calculatedVerifier = expectedVerifier.toString();
  
  return calculatedVerifier === verifier;
};

export const QRVerification = () => {
  const [activeTab, setActiveTab] = useState<'info' | 'checklist'>('info');
  const [currentCategory, setCurrentCategory] = useState(0);
  const [formData, setFormData] = useState<VerificationForm>({
    accessCode: '',
    nombre: '',
    rut: ''
  });
  const [anomaliaForm, setAnomaliaForm] = useState<AnomaliaForm>({
    descripcion: '',
    fotos: []
  });
  const [showAnomaliaModal, setShowAnomaliaModal] = useState(false);
  const [isSubmittingAnomalia, setIsSubmittingAnomalia] = useState(false);
  const [error, setError] = useState('');
  const [maquinaria, setMaquinaria] = useState<Maquinaria | null>(null);
  const [horometro, setHorometro] = useState<Horometro | null>(null);
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [searchParams] = useSearchParams();
  const [isVerified, setIsVerified] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Documento | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
  const [isSubmittingChecklist, setIsSubmittingChecklist] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [selectedOperator, setSelectedOperator] = useState<string>('');
  const [operatorError, setOperatorError] = useState<string>('');
  const [operatorComments, setOperatorComments] = useState('');

  const { id } = useParams();
  const navigate = useNavigate();
  const qrCode = searchParams.get('qr');

  // Inicializar checklist con categorías
  useEffect(() => {
    const items: ChecklistItem[] = [];
    let idCounter = 1;

    RIESGOS_MATERIALES.forEach(item => {
      items.push({
        id: `item-${idCounter++}`,
        nombre: item,
        estado: null,
        observaciones: '',
        categoria: 'seguridad'
      });
    });

    ESTADO_DESPUES_VUELTA.forEach(item => {
      items.push({
        id: `item-${idCounter++}`,
        nombre: item,
        estado: null,
        observaciones: '',
        categoria: 'estado_vuelta'
      });
    });

    OTROS_CONTROLES.forEach(item => {
      items.push({
        id: `item-${idCounter++}`,
        nombre: item,
        estado: null,
        observaciones: '',
        categoria: 'otros_controles'
      });
    });

    CONDICION_OPERACIONAL.forEach(item => {
      items.push({
        id: `item-${idCounter++}`,
        nombre: item,
        estado: null,
        observaciones: '',
        categoria: 'operacional'
      });
    });

    CONDICION_ESTRUCTURAL.forEach(item => {
      items.push({
        id: `item-${idCounter++}`,
        nombre: item,
        estado: null,
        observaciones: '',
        categoria: 'estructural'
      });
    });

    setChecklist(items);
  }, []);

  useEffect(() => {
    const fetchOperators = async () => {
      try {
        const { data, error } = await supabase
          .from('operators')
          .select('*')
          .eq('active', true)
          .order('name');

        if (error) throw error;
        setOperators(data || []);
      } catch (err) {
        console.error('Error fetching operators:', err);
      }
    };

    if (isVerified) {
      fetchOperators();
    }
  }, [isVerified]);

  useEffect(() => {
    const fetchMaquinariaData = async () => {
      if (!id) return;
      
      const { data: maquinariaData, error: maquinariaError } = await supabase
        .from('maquinarias')
        .select('*')
        .eq('id', id)
        .single();

      if (maquinariaData) {
        setMaquinaria(maquinariaData);
        if (qrCode) {
          setFormData(prev => ({ ...prev, accessCode: qrCode }));
        }

        const { data: horometroData } = await supabase
          .from('horometros')
          .select('*')
          .eq('maquinaria_id', id)
          .order('fecha_lectura', { ascending: false })
          .limit(1)
          .single();

        if (horometroData) {
          setHorometro(horometroData);
        }

        const { data: documentosData } = await supabase
          .from('documentos')
          .select('*')
          .eq('maquinaria_id', id);

        if (documentosData) {
          setDocumentos(documentosData);
        }
      }
    };

    fetchMaquinariaData();
  }, [id, qrCode]);

  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    value = value.replace(/\./g, '').replace(/-/g, '');
    value = value.replace(/[^0-9kK]/g, '');
    
    if (value.length > 9) {
      value = value.slice(0, 9);
    }
    
    if (value.length > 1) {
      const digits = value.slice(0, -1);
      const verifier = value.slice(-1);
      const formattedDigits = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      value = `${formattedDigits}-${verifier}`;
    }
    
    setFormData(prev => ({ ...prev, rut: value }));
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const cleanRut = formData.rut.replace(/\./g, '').replace(/-/g, '');
    if (!validateRut(cleanRut)) {
      setError('El RUT ingresado no es válido');
      return;
    }

    if (formData.nombre.trim().length < 3) {
      setError('El nombre debe tener al menos 3 caracteres');
      return;
    }

    try {
      const { data: maquinariaData, error: maquinariaError } = await supabase
        .from('maquinarias')
        .select('id')
        .eq('id', id)
        .eq('clave_qr', formData.accessCode)
        .single();

      if (maquinariaError || !maquinariaData) {
        setError('Código de acceso inválido');
        return;
      }

      const { error: verificationError } = await supabase
        .from('verificaciones')
        .insert([{
          maquinaria_id: id,
          nombre: formData.nombre,
          rut: formData.rut
        }]);

      if (verificationError) {
        console.error('Error storing verification:', verificationError);
        setError('Error al registrar la verificación');
        return;
      }

      setIsVerified(true);
    } catch (err) {
      console.error('Error during verification:', err);
      setError('Error al verificar el código');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setAnomaliaForm(prev => ({
        ...prev,
        fotos: [...prev.fotos, ...newFiles]
      }));
    }
  };

  const removeFile = (index: number) => {
    setAnomaliaForm(prev => ({
      ...prev,
      fotos: prev.fotos.filter((_, i) => i !== index)
    }));
  };

  const handleAnomaliaSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!maquinaria || !formData.nombre || !formData.rut) return;

    setIsSubmittingAnomalia(true);
    setError('');

    try {
      const { data: hallazgo, error: hallazgoError } = await supabase
        .from('hallazgos')
        .insert({
          maquinaria_id: maquinaria.id,
          descripcion: anomaliaForm.descripcion,
          created_by: `${formData.nombre} (${formData.rut})`
        })
        .select()
        .single();

      if (hallazgoError) throw hallazgoError;

      const createdAt = new Date(hallazgo.created_at);
      const folderName = createdAt.toISOString().replace(/[:.]/g, '-');
      const folderPath = `${maquinaria.id}/${folderName}`;
      
      for (const [index, foto] of anomaliaForm.fotos.entries()) {
        const fileExt = foto.name.split('.').pop();
        const fileName = `photo-${index + 1}.${fileExt}`;
        const filePath = `${folderPath}/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('hallazgos-fotos')
          .upload(filePath, foto);

        if (uploadError) throw uploadError;
      }

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-hallazgo-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hallazgoId: hallazgo.id
        })
      });

      if (!response.ok) {
        console.error('Error sending notification:', await response.text());
      }

      setShowAnomaliaModal(false);
      setAnomaliaForm({ descripcion: '', fotos: [] });
      setShowSuccessMessage(true);
      
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } catch (err) {
      console.error('Error submitting hallazgo:', err);
      setError('Error al registrar el hallazgo');
    } finally {
      setIsSubmittingAnomalia(false);
    }
  };

  const handleChecklistSubmit = async () => {
    if (!maquinaria || !formData.nombre || !formData.rut) return;

    if (!selectedOperator) {
      setOperatorError('Debe seleccionar un operador');
      return;
    }

    // Validar que todos los items estén completados
    const incompleteItems = checklist.filter(item => !item.estado);
    if (incompleteItems.length > 0) {
      setError(`Debe completar todos los items del checklist (${incompleteItems.length} pendientes)`);
      return;
    }

    setIsSubmittingChecklist(true);
    setError('');
    setOperatorError('');

    try {
      const { data: checklistData, error: checklistError } = await supabase
        .from('checklists')
        .insert({
          maquinaria_id: maquinaria.id,
          operator_id: selectedOperator,
          fecha: new Date().toISOString(),
          comments: operatorComments
        })
        .select()
        .single();

      if (checklistError) throw checklistError;

      const checklistItems = checklist.map(item => ({
        checklist_id: checklistData.id,
        nombre: item.nombre,
        estado: item.estado,
        observaciones: item.observaciones || ''
      }));

      const { error: itemsError } = await supabase
        .from('checklist_items')
        .insert(checklistItems);

      if (itemsError) throw itemsError;

      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-checklist-email`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            checklistId: checklistData.id
          })
        });

        if (!response.ok) {
          console.error('Error sending notification:', await response.text());
        }
      } catch (emailError) {
        console.error('Error sending email notification:', emailError);
      }

      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowSuccessMessage(false);
        navigate('/');
      }, 3000);

    } catch (err) {
      console.error('Error submitting checklist:', err);
      setError('Error al guardar el checklist');
    } finally {
      setIsSubmittingChecklist(false);
    }
  };

  const handleChecklistItemUpdate = (id: string, field: keyof ChecklistItem, value: string | 'bueno' | 'regular' | 'malo') => {
    setChecklist(prev => prev.map(item => 
      item.id === id 
        ? { ...item, [field]: value }
        : item
    ));
  };

  // Calcular progreso
  const calculateProgress = () => {
    const totalItems = checklist.length;
    const completedItems = checklist.filter(item => item.estado !== null).length;
    const percentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

    const categoryProgress = CHECKLIST_CATEGORIES.map(category => {
      const categoryItems = checklist.filter(item => item.categoria === category.id);
      const categoryCompleted = categoryItems.filter(item => item.estado !== null).length;
      return {
        ...category,
        total: categoryItems.length,
        completed: categoryCompleted,
        percentage: categoryItems.length > 0 ? (categoryCompleted / categoryItems.length) * 100 : 0
      };
    });

    return { totalItems, completedItems, percentage, categoryProgress };
  };

  const progress = calculateProgress();
  const currentCategoryData = CHECKLIST_CATEGORIES[currentCategory];
  const currentCategoryItems = checklist.filter(item => item.categoria === currentCategoryData.id);

  const renderChecklistItem = (item: ChecklistItem, isSecurityRisk: boolean) => {
    return (
      <div 
        key={item.id}
        className={clsx(
          "bg-white rounded-xl transition-all duration-200",
          item.estado === 'malo' ? 'ring-2 ring-red-300 shadow-lg' :
          item.estado === 'regular' ? 'ring-2 ring-yellow-300 shadow-lg' :
          item.estado === 'bueno' ? 'ring-2 ring-green-200 shadow-md' :
          'shadow-sm hover:shadow-md border border-gray-200'
        )}
      >
        <div className="p-5">
          <div className="flex items-start gap-4">
            <div className="flex-grow">
              <p className="text-sm font-medium text-gray-800 leading-relaxed">{item.nombre}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isSecurityRisk ? (
                <>
                  <button
                    onClick={() => handleChecklistItemUpdate(item.id, 'estado', 'bueno')}
                    className={clsx(
                      'relative w-12 h-12 rounded-full transition-all duration-200 focus:outline-none focus:ring-4',
                      item.estado === 'bueno'
                        ? 'bg-green-500 ring-2 ring-green-300 shadow-lg transform scale-110'
                        : 'bg-white border-2 border-green-400 hover:bg-green-50 hover:border-green-500'
                    )}
                    title="Puede seguir operando"
                  >
                    {item.estado === 'bueno' && (
                      <CheckCircle2 className="w-6 h-6 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                  <button
                    onClick={() => handleChecklistItemUpdate(item.id, 'estado', 'malo')}
                    className={clsx(
                      'relative w-12 h-12 rounded-full transition-all duration-200 focus:outline-none focus:ring-4',
                      item.estado === 'malo'
                        ? 'bg-red-500 ring-2 ring-red-300 shadow-lg transform scale-110'
                        : 'bg-white border-2 border-red-400 hover:bg-red-50 hover:border-red-500'
                    )}
                    title="Debe DETENER la tarea"
                  >
                    {item.estado === 'malo' && (
                      <X className="w-6 h-6 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => handleChecklistItemUpdate(item.id, 'estado', 'bueno')}
                    className={clsx(
                      'relative w-12 h-12 rounded-full transition-all duration-200 focus:outline-none focus:ring-4',
                      item.estado === 'bueno'
                        ? 'bg-green-500 ring-2 ring-green-300 shadow-lg transform scale-110'
                        : 'bg-white border-2 border-green-400 hover:bg-green-50 hover:border-green-500'
                    )}
                    title="Bueno"
                  >
                    {item.estado === 'bueno' && (
                      <CheckCircle2 className="w-6 h-6 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                  <button
                    onClick={() => handleChecklistItemUpdate(item.id, 'estado', 'regular')}
                    className={clsx(
                      'relative w-12 h-12 rounded-full transition-all duration-200 focus:outline-none focus:ring-4',
                      item.estado === 'regular'
                        ? 'bg-yellow-500 ring-2 ring-yellow-300 shadow-lg transform scale-110'
                        : 'bg-white border-2 border-yellow-400 hover:bg-yellow-50 hover:border-yellow-500'
                    )}
                    title="Regular"
                  >
                    {item.estado === 'regular' && (
                      <AlertTriangle className="w-6 h-6 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                  <button
                    onClick={() => handleChecklistItemUpdate(item.id, 'estado', 'malo')}
                    className={clsx(
                      'relative w-12 h-12 rounded-full transition-all duration-200 focus:outline-none focus:ring-4',
                      item.estado === 'malo'
                        ? 'bg-red-500 ring-2 ring-red-300 shadow-lg transform scale-110'
                        : 'bg-white border-2 border-red-400 hover:bg-red-50 hover:border-red-500'
                    )}
                    title="Malo"
                  >
                    {item.estado === 'malo' && (
                      <X className="w-6 h-6 text-white absolute inset-0 m-auto" />
                    )}
                  </button>
                </>
              )}
            </div>
          </div>
          {item.estado && item.estado !== 'bueno' && (
            <div className={clsx(
              "mt-4 pt-4 border-t transition-all duration-200",
              item.estado === 'malo' ? 'border-red-200' : 'border-yellow-200'
            )}>
              <textarea
                value={item.observaciones}
                onChange={(e) => handleChecklistItemUpdate(item.id, 'observaciones', e.target.value)}
                placeholder={item.estado === 'malo' 
                  ? "Describa el problema encontrado (requerido)..." 
                  : "Agregar observaciones (opcional)..."}
                className={clsx(
                  "w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-2 focus:outline-none text-sm transition-colors",
                  item.estado === 'malo' 
                    ? 'border-red-300 focus:border-red-500 focus:ring-red-200 placeholder-red-400' 
                    : 'border-yellow-300 focus:border-yellow-500 focus:ring-yellow-200 placeholder-yellow-600'
                )}
                rows={2}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!maquinaria) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004A98]"></div>
          <p className="mt-4 text-gray-600">Cargando información...</p>
        </div>
      </div>
    );
  }

  if (isVerified) {
    return (
      <div className="min-h-screen bg-gray-50">
        {showSuccessMessage && (
          <div className="fixed top-4 right-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center shadow-lg z-50">
            <CheckCircle2 className="h-5 w-5 mr-2" />
            {activeTab === 'checklist' ? 'Checklist guardado correctamente' : 'Hallazgo registrado correctamente'}
          </div>
        )}

        <div className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="py-4">
              <img 
                src="https://emin.cl/wp-content/uploads/2025/Logo-Caemin-Grupo-Emin.png" 
                alt="CAEMIN Logo" 
                className="h-12"
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Tabs */}
          <div className="bg-white shadow-sm rounded-lg overflow-hidden mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex">
                <button
                  onClick={() => setActiveTab('info')}
                  className={clsx(
                    'w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors',
                    activeTab === 'info'
                      ? 'border-[#004A98] text-[#004A98]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <Truck className="h-5 w-5 inline-block mr-2" />
                  Información
                </button>
                <button
                  onClick={() => setActiveTab('checklist')}
                  className={clsx(
                    'w-1/2 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors',
                    activeTab === 'checklist'
                      ? 'border-[#004A98] text-[#004A98]'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  <ClipboardCheck className="h-5 w-5 inline-block mr-2" />
                  Checklist
                </button>
              </nav>
            </div>
          </div>

          {activeTab === 'info' ? (
            <div className="space-y-6">
              {/* Información de la maquinaria */}
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-6">{maquinaria.nombre}</h1>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Información General</h3>
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4">
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Código</dt>
                        <dd className="mt-1 text-sm text-gray-900">{maquinaria.codigo}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-gray-500">Tipo</dt>
                        <dd className="mt-1 text-sm text-gray-900">{maquinaria.tipo}</dd>
                      </div>
                      {maquinaria.patente && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Patente</dt>
                          <dd className="mt-1 text-sm text-gray-900">{maquinaria.patente}</dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Detalles del Vehículo</h3>
                    <dl className="grid grid-cols-1 gap-x-4 gap-y-4">
                      {maquinaria.marca && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Marca</dt>
                          <dd className="mt-1 text-sm text-gray-900">{maquinaria.marca}</dd>
                        </div>
                      )}
                      {maquinaria.modelo && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Modelo</dt>
                          <dd className="mt-1 text-sm text-gray-900">{maquinaria.modelo}</dd>
                        </div>
                      )}
                      {maquinaria.anio && (
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Año</dt>
                          <dd className="mt-1 text-sm text-gray-900">{maquinaria.anio}</dd>
                        </div>
                      )}
                    </dl>
                  </div>
                </div>

                {horometro && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Estado del Horómetro</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600">Lectura actual:</span>
                        <span className="font-medium text-gray-900">{horometro.lectura} hrs</span>
                      </div>
                      <div className="mt-2">
                        <div className="relative pt-1">
                          <div className="overflow-hidden h-2 text-xs flex rounded bg-gray-200">
                            <div
                              style={{
                                width: `${((horometro.lectura % horometro.alerta_mantenimiento) / horometro.alerta_mantenimiento) * 100}%`
                              }}
                              className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-[#004A98]"
                            ></div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-2 text-sm text-gray-500">
                        Próximo mantenimiento a las {calculateNextMaintenance(horometro.lectura, horometro.alerta_mantenimiento)} hrs
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex justify-end">
                  <button
                    onClick={() => setShowAnomaliaModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <AlertCircle className="h-5 w-5 mr-2" />
                    Reportar Hallazgo
                  </button>
                </div>
              </div>

              {/* Sección de Documentos del Vehículo */}
              {documentos.length > 0 && (
                <div className="bg-white shadow-sm rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Documentos del Vehículo</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                            Documento
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Estado
                          </th>
                          <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                            Vencimiento
                          </th>
                          <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                            <span className="sr-only">Acciones</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {documentos.map((documento) => {
                          const status = getDocumentStatus(documento);
                          return (
                            <tr key={documento.id}>
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                <div className="flex items-center">
                                  <FileText className="h-5 w-5 text-gray-400 mr-2" />
                                  {documento.tipo}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  status.color === 'red' ? 'bg-red-100 text-red-800' :
                                  status.color === 'yellow' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {status.status === 'missing' && <AlertCircle className="h-4 w-4 mr-1" />}
                                  {status.status === 'valid' && <CheckCircle2 className="h-4 w-4 mr-1" />}
                                  {status.status === 'warning' && <Clock className="h-4 w-4 mr-1" />}
                                  {status.status === 'expired' && <X className="h-4 w-4 mr-1" />}
                                  {status.text}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {formatDate(documento.fecha_vencimiento)}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                {documento.storage_path && (
                                  <button
                                    onClick={() => setSelectedDocument(documento)}
                                    className="text-[#004A98] hover:text-[#003875]"
                                  >
                                    Ver documento
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Selección de operador */}
              <div className="bg-white shadow-sm rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Seleccionar Operador</h3>
                <select
                  value={selectedOperator}
                  onChange={(e) => {
                    setSelectedOperator(e.target.value);
                    setOperatorError('');
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                >
                  <option value="">Seleccionar operador</option>
                  {operators.map((operator) => (
                    <option key={operator.id} value={operator.id}>
                      {operator.name} ({operator.rut})
                    </option>
                  ))}
                </select>
                {operatorError && (
                  <p className="mt-2 text-sm text-red-600">{operatorError}</p>
                )}
              </div>

              {/* Barra de progreso general */}
              <div className="bg-white shadow-sm rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Progreso del Checklist</h3>
                  <span className="text-sm font-medium text-gray-600">
                    {progress.completedItems} de {progress.totalItems} items completados
                  </span>
                </div>
                <div className="relative">
                  <div className="overflow-hidden h-3 text-xs flex rounded-full bg-gray-200">
                    <div
                      style={{ width: `${progress.percentage}%` }}
                      className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-blue-500 to-[#004A98] transition-all duration-300"
                    />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-5 gap-2">
                  {progress.categoryProgress.map((cat, index) => (
                    <button
                      key={cat.id}
                      onClick={() => setCurrentCategory(index)}
                      className={clsx(
                        "p-3 rounded-lg text-center transition-all",
                        currentCategory === index 
                          ? "bg-[#004A98] text-white shadow-lg" 
                          : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                      )}
                    >
                      <cat.icon className="h-5 w-5 mx-auto mb-1" />
                      <div className="text-xs font-medium">{cat.completed}/{cat.total}</div>
                      <div className="text-xs opacity-75">{Math.round(cat.percentage)}%</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Categoría actual */}
              <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                <div className="bg-[#27427A] text-white px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <currentCategoryData.icon className="h-6 w-6" />
                      <div>
                        <h2 className="text-lg font-semibold">{currentCategoryData.name}</h2>
                        <p className="text-sm text-blue-100">
                          {progress.categoryProgress[currentCategory].completed} de {progress.categoryProgress[currentCategory].total} completados
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {currentCategoryData.isSecurityRisk ? (
                        <div className="flex items-center gap-3 bg-white/10 px-4 py-2 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-green-500 ring-2 ring-green-300"></div>
                            <span className="text-sm">Operativo</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-red-500 ring-2 ring-red-300"></div>
                            <span className="text-sm">Detener</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 bg-white/10 px-4 py-2 rounded-lg">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-green-500 ring-2 ring-green-300"></div>
                            <span className="text-sm">Bueno</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-yellow-500 ring-2 ring-yellow-300"></div>
                            <span className="text-sm">Regular</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-red-500 ring-2 ring-red-300"></div>
                            <span className="text-sm">Malo</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="p-6 bg-gray-50">
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    {currentCategoryItems.map(item => renderChecklistItem(item, currentCategoryData.isSecurityRisk))}
                  </div>
                </div>

                {/* Navegación entre categorías */}
                <div className="bg-white border-t border-gray-200 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => setCurrentCategory(Math.max(0, currentCategory - 1))}
                      disabled={currentCategory === 0}
                      className={clsx(
                        "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all",
                        currentCategory === 0 
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed" 
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      )}
                    >
                      <ChevronLeft className="h-5 w-5" />
                      Anterior
                    </button>

                    {currentCategory === CHECKLIST_CATEGORIES.length - 1 ? (
                      <button
                        onClick={handleChecklistSubmit}
                        disabled={isSubmittingChecklist || progress.completedItems < progress.totalItems}
                        className={clsx(
                          "flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-all",
                          progress.completedItems < progress.totalItems
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-green-600 text-white hover:bg-green-700 shadow-lg"
                        )}
                      >
                        {isSubmittingChecklist ? (
                          <>
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                            Guardando...
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="h-5 w-5" />
                            Finalizar y Guardar
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => setCurrentCategory(Math.min(CHECKLIST_CATEGORIES.length - 1, currentCategory + 1))}
                        className="flex items-center gap-2 px-4 py-2 bg-[#004A98] text-white rounded-lg hover:bg-[#003875] font-medium transition-all"
                      >
                        Siguiente
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Comentarios del operador */}
              {currentCategory === CHECKLIST_CATEGORIES.length - 1 && (
                <div className="bg-white shadow-sm rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Comentarios del Operador</h3>
                  <textarea
                    value={operatorComments}
                    onChange={(e) => setOperatorComments(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:border-[#004A98] focus:ring-[#004A98]"
                    rows={4}
                    placeholder="Ingrese sus comentarios aquí..."
                  />
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal de anomalía */}
        {showAnomaliaModal && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Registrar Hallazgo</h3>
                <button
                  onClick={() => setShowAnomaliaModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleAnomaliaSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Descripción del hallazgo
                  </label>
                  <textarea
                    value={anomaliaForm.descripcion}
                    onChange={(e) => setAnomaliaForm(prev => ({ ...prev, descripcion: e.target.value }))}
                    rows={4}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                    placeholder="Describe el hallazgo detectado..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Fotos del hallazgo
                  </label>
                  <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                    <div className="space-y-1 text-center">
                      <Camera className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer bg-white rounded-md font-medium text-[#004A98] hover:text-[#003875] focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-[#004A98]"
                        >
                          <span>Subir fotos</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            className="sr-only"
                            accept="image/*"
                            multiple
                            onChange={handleFileChange}
                          />
                        </label>
                        <p className="pl-1">o arrastra y suelta</p>
                      </div>
                      <p className="text-xs text-gray-500">
                        PNG, JPG, GIF hasta 10MB
                      </p>
                    </div>
                  </div>

                  {anomaliaForm.fotos.length > 0 && (
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      {anomaliaForm.fotos.map((foto, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(foto)}
                            alt={`Foto ${index + 1}`}
                            className="h-24 w-full object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="absolute top-0 right-0 -mt-2 -mr-2 bg-red-100 rounded-full p-1 text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAnomaliaModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmittingAnomalia}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  >
                    {isSubmittingAnomalia ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Registrando...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Registrar hallazgo
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal para ver documento */}
        {selectedDocument && (
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{selectedDocument.tipo}</h3>
                <button
                  onClick={() => setSelectedDocument(null)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <DocumentViewer documento={selectedDocument} />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Login form
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-md mx-auto pt-12 px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <img 
            src="https://emin.cl/wp-content/uploads/2025/Logo-Caemin-Grupo-Emin.png" 
            alt="CAEMIN Logo" 
            className="mx-auto h-16"
          />
        </div>
        
        <div className="bg-white shadow-lg rounded-lg p-8">
          <div className="flex justify-center mb-6">
            <Truck className="h-16 w-16 text-[#004A98]" />
          </div>
          
          <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Verificación de Acceso
          </h2>
          
          <div className="text-center mb-6">
            <p className="text-lg font-medium text-gray-900">{maquinaria.nombre}</p>
            <p className="text-sm text-gray-500">{maquinaria.tipo}</p>
          </div>

          <form onSubmit={handleVerification} className="space-y-6">
            <div>
              <label htmlFor="nombre" className="block text-sm font-medium text-gray-700">
                Nombre Completo
              </label>
              <div className="mt-1">
                <input
                  id="nombre"
                  type="text"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#004A98] focus:border-[#004A98] sm:text-sm"
                  placeholder="Ingresa tu nombre completo"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label htmlFor="rut" className="block text-sm font-medium text-gray-700">
                RUT
              </label>
              <div className="mt-1">
                <input
                  id="rut"
                  type="text"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#004A98] focus:border-[#004A98] sm:text-sm"
                  placeholder="12.345.678-9"
                  value={formData.rut}
                  onChange={handleRutChange}
                  maxLength={12}
                />
              </div>
            </div>

            <div>
              <label htmlFor="access-code" className="block text-sm font-medium text-gray-700">
                Código de acceso
              </label>
              <div className="mt-1">
                <input
                  id="access-code"
                  type="text"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#004A98] focus:border-[#004A98] sm:text-sm"
                  placeholder="Ingresa el código de acceso"
                  value={formData.accessCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, accessCode: e.target.value }))}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#004A98] hover:bg-[#003875] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#004A98]"
            >
              Verificar
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};